import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  Rectangle,
} from 'recharts';
import moment from 'moment';
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { FHSkeletonLoader } from '../../../base';
import {
  FORTA_BLUE,
  FORTA_ERROR,
  FORTA_SUCCESS,
  FORTA_WHITE,
  FORTA_ORANGE,
  FORTA_GRAY2,
} from '../../../../utils/colors';

import type { ITherapyUtilization } from '../../../../utils/types';
import type { TFunction } from 'i18next';

function getBarColour(value: number) {
  if (!value) return FORTA_ERROR;
  if (value <= 50) {
    return FORTA_ERROR;
  }
  if (value > 50 && value <= 80) {
    return FORTA_ORANGE;
  }
  if (value > 80) {
    return FORTA_SUCCESS;
  }
  return FORTA_ERROR;
}

const CustomTooltip = (t: TFunction<'dashboard'>, { active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <Box
        sx={{
          backgroundColor: FORTA_WHITE,
          border: `1px solid ${FORTA_BLUE.primary}`,
          p: 1,
          borderRadius: '10px',
        }}
      >
        <p style={{ fontWeight: 'bold' }}>{`${t(
          'utilization.sixWeekUtilization.weekStartingWith',
        )} ${label}`}</p>
        <p>{`${t('utilization.sixWeekUtilization.utilization')}: ${payload[0].value}%`}</p>
      </Box>
    );
  }

  return null;
};

function UtilizationLoader() {
  return (
    <Box
      data-testid="UtilizationBarChart-loader"
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        px: 5,
        ml: 8.5,
        height: '95%',
        borderLeft: `1px solid ${FORTA_GRAY2}`,
        borderBottom: `1px solid ${FORTA_GRAY2}`,
      }}
    >
      <FHSkeletonLoader
        rowCount={6}
        variant="rectangular"
        containerStyles={{
          display: 'flex',
          gap: 2,
          width: '100%',
        }}
        sx={{
          width: '15%',
          height: '50%',
          alignSelf: 'flex-end',
        }}
      />
    </Box>
  );
}

interface UtilizationBarChartProps {
  utilization: ITherapyUtilization[];
  loading?: boolean;
}

function UtilizationBarChart({ utilization = [], loading }: UtilizationBarChartProps) {
  const { t } = useTranslation('dashboard');
  if (loading) {
    return <UtilizationLoader />;
  }

  const data = utilization.map((item) => ({
    name: moment(item?.week_starts_on).format('MMM Do'),
    utilization: item.utilization > 100 ? 100 : Math.ceil(item.utilization ?? 0),
  }));

  return data.length ? (
    <ResponsiveContainer width="95%" height="95%">
      <BarChart
        width={500}
        height={300}
        data={data}
        margin={{
          top: 5,
          right: 30,
          left: 20,
          bottom: 5,
        }}
      >
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="name" />
        <YAxis domain={[0, 100]} tickFormatter={(value) => `${value}%`} />
        <Tooltip
          cursor={{ fill: 'transparent' }}
          content={(data: any) => {
            return CustomTooltip(t, data);
          }}
        />
        <Bar dataKey="utilization" activeBar={<Rectangle stroke={FORTA_BLUE.primary} />}>
          {data.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={getBarColour(entry.utilization ?? 0)} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  ) : null;
}

export default UtilizationBarChart;
